import React, { useRef } from "react";
import styled from "styled-components";
import invariant from "tiny-invariant";
import { getDateIsoString } from "@lifeat/shared/util/DateUtil";
import { AmplitudeEventSource } from "@analytics/Amplitude/AmplitudeEvents";
import VirtualListItem from "@common/VirtualListItem/VirtualListItem";
import { TaskDropEventData } from "../../drag-and-drop/DragAndDropTypes";
import { useTaskListContext } from "../TaskListContext";
import { IdentifiableTask } from "../TaskManagementTypes";
import DraftTaskItem from "./DraftTaskItem";
import DroppableTaskSpot from "./DroppableTaskSpot";
import SingularTaskItem, { SingularTaskItemProps } from "./SingularTaskItem";

export const DEFAULT_ROW_HEIGHT = 51;

const RowContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const StyledDroppableTaskSpot = styled(DroppableTaskSpot)`
    padding: 2px 9px 2px 20px;
`;

export interface DateColumnProps {
    /**
     * The rendered tasks should be scheduled for that date.
     * Task Creation should create the task on that date.
     */
    readonly date: Date;
    /**
     * The task with the highest order rank. This is used when the tasks passed to
     * this component's instance are not comprehensive of all tasks, in order to create new tasks
     * from a Date Column with the correct (highest) order rank. The taskList prop may only
     * contain a subset of tasks (or no tasks), which will not allow for the correct
     * calculation of a new task's orderRank.
     */
    readonly highestOrderRankTask: IdentifiableTask | null;
}

type RowProps = {
    index: number;
    viewportRef: React.RefObject<HTMLDivElement | null>;
    data: {
        orderedTasks: IdentifiableTask[];
        amplitudeEventSource: AmplitudeEventSource;
        dateColumnProps?: DateColumnProps;
        onNewDraftTask: (lowerBoundTaskId?: string, upperBoundTaskId?: string) => void;
        onTaskDrop: (data: TaskDropEventData, date?: Date) => void;
    };
};

export function Row({
    index,
    viewportRef,
    data: { orderedTasks, amplitudeEventSource, dateColumnProps, onNewDraftTask, onTaskDrop },
}: RowProps) {
    const { filters, taskFocusEvent$, createTask } = useTaskListContext();
    const rowRef = useRef<HTMLDivElement | null>(null);

    const handleKeyPress = (
        e: React.KeyboardEvent<HTMLTextAreaElement>,
        currentTaskId: string,
        lowerBoundTaskId?: string,
        upperBoundTaskId?: string,
    ) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            e.stopPropagation();

            onNewDraftTask(lowerBoundTaskId, currentTaskId);
        } else if (e.key === "Tab") {
            e.preventDefault();
            e.stopPropagation();
            if (e.shiftKey) {
                // If shift is held while tabbing, focus on previous task
                if (upperBoundTaskId) {
                    taskFocusEvent$.value = {
                        type: "singular",
                        taskId: upperBoundTaskId,
                    };
                }
            } else {
                // If shift not held, focus on next task
                if (lowerBoundTaskId) {
                    taskFocusEvent$.value = {
                        type: "singular",
                        taskId: lowerBoundTaskId,
                    };
                }
            }
        }
    };

    const task = orderedTasks[index];
    invariant(task, "Error task in taskList data doesnt exist");
    const id = task.id;

    const upperBoundTaskId = orderedTasks[index - 1]?.id;
    const lowerBoundTaskId = orderedTasks[index + 1]?.id;

    const taskItemProps: Omit<SingularTaskItemProps, "index"> & { key: string } = {
        key: `task-${task.document.clientId}`,
        taskId: id,
        clientId: task.document.clientId,
        task: task.document,
        handleKeyPress: (e) => handleKeyPress(e, id, lowerBoundTaskId, upperBoundTaskId),
        isTaskInFocus: taskFocusEvent$.value?.type === "singular" && taskFocusEvent$.value.taskId === id,
        amplitudeEventSource: amplitudeEventSource,
    };

    const todayFilter = filters.filter((filter) => filter.type === "today")?.[0];
    const scheduledDate = todayFilter ? new Date() : dateColumnProps?.date;
    const previousItem = orderedTasks[index - 1];

    const droppableTaskSpot = (
        <StyledDroppableTaskSpot
            key={`drop-task-spot-${index}`}
            onDrop={(data: TaskDropEventData) => onTaskDrop(data, dateColumnProps?.date)}
            lowerBoundTask={task}
            upperBoundTask={previousItem}
        />
    );

    if (task.document.isDraft) {
        return (
            <RowContainer ref={rowRef}>
                {droppableTaskSpot}
                <DraftTaskItem
                    {...taskItemProps}
                    handleCreateTask={(updatedTitle: string) =>
                        createTask({
                            title: updatedTitle,
                            clientId: id,
                            lowerBoundTaskId: lowerBoundTaskId,
                            upperBoundTaskId: upperBoundTaskId,
                            scheduleDateStr: scheduledDate && getDateIsoString(scheduledDate),
                            tagId: task.document.tagId,
                            projectId: task.document.projectId ?? undefined,
                        })
                    }
                />
            </RowContainer>
        );
    }
    return (
        <VirtualListItem viewportRef={viewportRef} defaultHeight={79}>
            <RowContainer ref={rowRef}>
                {droppableTaskSpot}
                <SingularTaskItem {...taskItemProps} index={index} />
            </RowContainer>
        </VirtualListItem>
    );
}
